import type { FundraisingStatus } from "@entrilia/shared/api/fundraisingTypes";
import type { RangeRequestParams } from "@entrilia/shared/api/types";
import type { PortalRole } from "./accessTypes";
import type {
  InvestorPortalMessageSource,
  MessagePublishStatus,
  MessagePublishStatusFilter,
} from "./documentCollectionTypes";
import type { ObjectWithFieldValuesBase } from "./objectTypes";

export interface ResendContactInvitationRequest {
  messageRequestId: string;
  userId: string;
}

export interface ResetUserPasswordResponse {
  tempPassword: string;
}

export interface ChangeContactEmailAndFundPortalLoginRequest {
  newEmail: string;
  newName?: string;
  sendInvitation: boolean;
  isSamePerson: boolean;
  historyTransferConfirmed: boolean;
}

export interface ChangeContactEmailAndFundPortalLoginResponse {
  email: string;
  name: string;
  invitationSent: boolean;
  outcome: ContactEmailChangeOutcome;
}

export type ContactEmailChangeOutcome = "Rename" | "Split" | "Attach";

export interface ContactEmailChangePlan {
  canChange: boolean;
  samePersonOutcomeAtFreeAddress?: ContactEmailChangeOutcome;
  blockReason?: ContactEmailChangeBlockReason;
}

export type ContactEmailChangeBlockReason = "RegistrationByEmailInProgress" | "PortalReachUnknown";

export interface ContactEmailAvailability {
  ownership: ContactEmailOwnership;
}

export type ContactEmailOwnership = "Unknown" | "Free" | "Taken";

export enum ContactStatus {
  NotRegistered = 1,
  Registered = 2,
}

export type EmailRegistrationEligibilityStatus =
  | "Eligible"
  | "NotInvited"
  | "NoEmail"
  | "AlreadyRegistered"
  | "InProgressAwaitingReply"
  | "InProgressSettingUp"
  | "InProgressAwaitingFirstSignIn"
  | "Expired"
  | "Rejected"
  | "RejectedAccessRemoved";

export interface EmailRegistrationEligibilityResponse {
  status: EmailRegistrationEligibilityStatus;
}

export type ContactWarningCode = "LastEmailDeliveryFailed";

export interface Contact {
  id: string;
  name: string;
  email: string;
  investorPortalLogin?: string;
  status: ContactStatus;
  lastActivity?: string;
  hasUndatedActivity?: boolean;
  invitationSentAt?: string;
  registrationDate?: string;
  hasAccessToInvestorPortal: boolean;
  hasAccessToPortfolioPortal: boolean;
  totalLiveMessages: number;
  warningsCodes: ContactWarningCode[];
  createdAt: string;
  updateAt: string;
}

export interface ContactWithFieldValues extends Contact, ObjectWithFieldValuesBase {
  fundInvestors: FundInvestorInfo[];
}

interface FundInvestorInfo {
  id: string;
  investorName: string;
  fundName: string;
}

export interface SearchContactsRequest {
  fieldIds?: string[];
  includeFundInvestors: boolean;
  includeInboxData: boolean;
  searchTerm?: string;
  investorIds?: string[];
  fundIds?: string[];
  statuses?: number[];
  hasAccessToInvestorPortal?: boolean;
  invitationSentFrom?: string;
  invitationSentTo?: string;
  lastActivityFrom?: string;
  lastActivityTo?: string;
  liveDocumentsFrom?: number;
  liveDocumentsTo?: number;
  isNotInvited?: boolean;
}

export interface FundStructurePermissions {
  roles: PortalRole[];
  categories: string[];
  isPrimary: boolean;
  emailNotification: boolean;
}

export interface ContactPermissions extends FundStructurePermissions {
  investorId: string;
  investorName: string;
  fundId: string;
  fundName: string;
  fundInvestorId: string;
}

export interface InvestorContactPermissions extends FundStructurePermissions {
  contactId: string;
  contactName: string;
  contactEmail?: string;
  fundName: string;
  fundInvestorId: string;
}
export interface FundContactPermissions extends FundStructurePermissions {
  contactId: string;
  name: string;
  email?: string;
}

export interface FundPermissions {
  investorName: string;
  investorId: string;
  fundInvestorId: string;
  contacts: FundContactPermissions[];
}

export interface InvestorCommunicationMatrixUpdate {
  fundInvestorId: string;
  contactId: string;
  isPrimary?: boolean;
  emailNotification?: boolean;
  roles?: string[];
  categories?: string[];
}

export interface InvestorCommunicationMatrixRemove {
  fundInvestorId: string;
  contactId: string;
}

export interface InvestorCommunicationMatrixCreate {
  investorName: string;
  investorId?: string;
  contactId?: string;
  isPrimary: boolean;
  emailNotification: boolean;
  roles: string[];
  categories: string[];
}

export interface UpdateInvestorCommunicationMatrixRequest {
  updates: InvestorCommunicationMatrixUpdate[];
  removes: InvestorCommunicationMatrixRemove[];
  creates: InvestorCommunicationMatrixCreate[];
}

export interface InvestorPermissions {
  investorId: string;
  investorName: string;
  permissions: InvestorContactPermissions[];
}

export type PasswordResetDisabledReason =
  | "NotRegistered"
  | "RegistrationByEmailInProgress"
  | "PortalAccessInAnotherOrganization"
  | "MissingResetPermissionInOtherClients";

export interface ContactPasswordReset {
  canReset: boolean;
  disabledReason?: PasswordResetDisabledReason;
}

export type UserMfaStatus = "NoMFA" | "ConditionalMFA" | "MandatoryMFA";

export type ContactRegistrationStory =
  | "NotRegistered"
  | "RegisteredAndActive"
  | "RegisteredAwaitingFirstSignIn"
  | "AlreadyRegisteredOnEntrilia"
  | "RegistrationByEmailInProgress";

export type UserAccountStatus = "Unknown" | "Enabled" | "Disabled";

export type UserSignInIssueType = "RepeatedAttempts" | "UnsafeAttempt" | "TemporaryBlock" | "PasswordChangeRequired";

export interface ContactSignInIssue {
  type: UserSignInIssueType;
  occurredAt: string;
}

export interface ContactSignInState {
  accountStatus: UserAccountStatus;
  recentIssue?: ContactSignInIssue;
}

export type EmailSuppressionState = "Unknown" | "NotSuppressed" | "BounceSuppressed";

export type LastEmailSender = "Unknown" | "Portal" | "Client";

export type LastEmailKind = "Unknown" | "Invitation" | "OtherEmail";

export interface ContactLastEmailDelivery {
  failed: boolean;
  failedAt?: string;
  failureMessage?: string;
  reason?: string;
  sender: LastEmailSender;
  kind: LastEmailKind;
}

// For each field absent is always "unknown", never an answer.
export interface ContactPortalAccess {
  passwordReset?: ContactPasswordReset;
  registrationStory?: ContactRegistrationStory;
  emailRegistrationStatus?: EmailRegistrationEligibilityStatus;
  mfaStatus?: UserMfaStatus;
  signInState?: ContactSignInState;
  emailSuppressionState?: EmailSuppressionState;
  lastEmailDelivery?: ContactLastEmailDelivery;
  hasDocumentActivity?: boolean;
  emailChange?: ContactEmailChangePlan;
}

export interface ContactDetails {
  contact: Contact;
  permissions: ContactPermissions[];
  updateAt: string;
  createdAt: string;
}

export interface CreateContactRequest {
  name: string;
  email: string;
  phone?: string;
}

export interface SearchInvestorDocumentsRequest {
  range: RangeRequestParams;
  search?: string;
  fundIds?: string[];
  fundInvestorIds?: string[];
  categoryIds?: string[];
  publishingStatusFilter: MessagePublishStatusFilter;
  sentAtFrom?: string;
  sentAtTo?: string;
}

export interface InvestorDocumentsDownload {
  token: string;
  documentCount: number;
  isTruncated: boolean;
}

export interface InvestorDocumentsExport {
  downloadUrl: string;
  fileName: string;
  exportedRecordsCount: number;
  isTruncated: boolean;
}

export interface FundStructureDocument {
  id: string;
  name: string;
  postMessageRequestId: string;
  documentCollectionId?: string;
  documentCollectionName: string;
  documentCollectionNo?: number;
  messageSource: InvestorPortalMessageSource;
  categoryId: string;
  investorNames: string[];
  fundNames: string[];
  isViewed: boolean;
  viewedAt?: string;
  sentAt: string;
  status: MessagePublishStatus;
  publishToInvestorPortal?: boolean;
  attachmentIds: string[];
  attachmentsWithViews?: Record<string, FundStructureDocumentViews>;
}

export interface FundStructureDocumentViews {
  firstViewDate?: string;
  totalViews: number;
  totalDownloads: number;
  totalViewTimeSeconds: number;
  totalCompletionPercentage: number;
}

export interface FundraisingDocument {
  id: string;
  name: string;
  fundraisingId: string;
  fundraisingName: string;
  categoryId?: string;
  fundInvestors: DocumentFundInvestor[];
  isViewed: boolean;
  viewedAt?: string;
  sentAt: string;
  status: FundraisingStatus;
  views?: FundStructureDocumentViews;
}

export interface DocumentFundInvestor {
  fundId: string;
  fundName: string;
  investorId: string;
  investorName: string;
}

export interface InvestorContact {
  contactId: string;
  name: string;
  email?: string;
}

export interface InvestorFund {
  fundInvestorId: string;
  fundId: string;
  fundName: string;
}

export interface FundWithFieldValues extends Fund, ObjectWithFieldValuesBase {}

export interface SearchFundsRequest {
  fieldIds?: string[];
  includeInvestorCount: boolean;
}

export interface Investor {
  id: string;
  title: string;
  investorEntityType: string;
  funds: InvestorFund[];
  contacts: InvestorContact[];
  createdAt: string;
  updateAt: string;
}

export interface InvestorWithFieldValues extends Investor, ObjectWithFieldValuesBase {}

export interface SearchInvestorsRequest {
  fieldIds?: string[];
  includeContacts: boolean;
  includeFunds: boolean;
  includeLookupObjects?: boolean;
  investorIds?: string[];
}

export interface CreateInvestorRequest {
  name: string;
}

export interface CreateInvestorResponse {
  id: string;
  name: string;
}

export interface DeleteEntityResponse {
  isDeleted: boolean;
  displayErrorMessage?: string;
}

export interface CreateFundInvestorRequest {
  fundId: string;
  investorId?: string;
  newInvestorName?: string;
  newContacts: NewContactRequest[];
  existingContacts: ExistingContactRequest[];
}

export interface UpdateFundInvestorRequest {
  newContacts: NewContactRequest[];
  existingContacts: ExistingContactRequest[];
  removedContactIds: string[];
}

interface NewContactRequest {
  name: string;
  email: string;
  roles: PortalRole[];
  externalCategoryIds: string[];
}

interface ExistingContactRequest {
  contactId: string;
  roles: PortalRole[];
  externalCategoryIds: string[];
}

export interface InvestorInvestmentsRequest {
  reportingDate: string;
}

export interface InvestmentLine {
  id: string;
  isTotalRow: boolean;
  fundName: string;
  currencyCode?: string;
  commitment?: number;
  unfunded?: number;
  contributions?: number;
  distributions?: number;
  balance?: number;
  netIrr?: number;
}

export interface CashFlowLine {
  id: string;
  portalStatus: CashFlowPortalStatus;
  name: string;
  paymentStatus: CashFlowPaymentStatus;
  paymentStatusLive: CashFlowPaymentStatus;
  paymentStatusPreview: CashFlowPaymentStatus;
  investorName: string;
  fundName: string;
  dueDate?: string;
  currencyCode?: string;
  amount: number;
  impactOnUnfunded: number;
  details: CashFlowDetailLine[];
}

export type CashFlowPortalStatus = "Live" | "Preview" | "None";

export interface CashFlowDetailLine {
  id: string;
  description: string;
  currencyCode: string;
  amount: number;
  impactDirection: CashFlowUnfundedImpactDirection;
}

export type CashFlowPaymentStatus = "Unpaid" | "PartlyPaid" | "Paid";

export type CashFlowUnfundedImpactDirection = "None" | "Increases" | "Decreases";

export interface InvestorCashFlowsRequest {
  fundIds?: string[];
  portalStatuses?: CashFlowPortalStatus[];
  fromDueDate?: string;
  toDueDate?: string;
}

export interface Fund {
  id: string;
  name: string;
  investorsCount?: number;
}

export interface FundDetails {
  id: string;
  name: string;
  description: string;
  stage?: FundStage;
  createdAt: string;
  updateAt?: string;
}

export type FundStage = "Investment" | "Fundraising";

export interface FundPermissionsResponse {
  fundId: string;
  fundName: string;
  permissions: FundPermissions[];
}
